/**
 * FINANCE: the financier's side of the same marketplace (FR-U-CRD-008, FR-U-CRD-010, FR-U-CRD-011).
 *
 * ## WHAT A FINANCIER MAY AND MAY NOT DO HERE
 *
 * They may see borrowing requests that a borrower has OPENED FOR FINANCING, and offer terms on them.
 * They may not go looking for tokenized assets to lend against: there is no query on this screen or
 * behind it that starts from an asset, because the direction of travel is fixed by the credit
 * program (asset -> the owner wants financing -> a request -> open -> a financier sees it).
 *
 * A participant without a verified credit provider profile gets a refusal from the endpoint, not an
 * empty list. Showing them an empty marketplace would imply there was nothing to finance, which is a
 * different and untrue statement.
 *
 * ## THE THIN BORROWER VIEW IS DELIBERATE
 *
 * A financier sees a tier, a risk state and how long the borrower has been on the platform. Not a
 * name, not a phone number, not a document. A marketplace listing is not a reason to disclose a
 * participant's identity to every registered lender; the one that ends up funding gets what it needs
 * at approval, under the ordinary controls.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  api,
  ForbiddenError,
  LipaApiError,
  type FinancerOffer,
  type FinancerPortfolio,
  type FinancingOpportunity,
} from '@/lib/api';
import { PageHeader, SectionTitle, StatTile } from '@/components/dash';
import { StatusPill } from '@/components/status';
import { EmptyState, ErrorState, LoadingCard } from '@/components/states';
import { Reveal } from '@/components/motion';
import { fmtDateShort } from '@/lib/format';
import { formatWireAmount } from '@/lib/money';

const TABS = ['Opportunities', 'My financing', 'Portfolio'] as const;

const STAGE_LABEL: Record<string, string> = {
  interest_expressed: 'Interest expressed',
  awaiting_approval: 'Awaiting your approval',
  committed: 'Committed',
  disbursed: 'Disbursed',
  closed: 'Closed',
};

export default function CreditFinance() {
  const [tab, setTab] = useState<(typeof TABS)[number]>('Opportunities');
  const [opportunities, setOpportunities] = useState<FinancingOpportunity[] | null>(null);
  const [offers, setOffers] = useState<FinancerOffer[]>([]);
  const [portfolio, setPortfolio] = useState<FinancerPortfolio | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [o, f, p] = await Promise.all([
        api.financeMarket.opportunities(),
        api.financeMarket.offers(),
        api.financeMarket.portfolio(),
      ]);
      setOpportunities(o.opportunities);
      setOffers(f.offers);
      setPortfolio(p);
    } catch (e) {
      // A refusal here is not a failure. It means this participant is not a credit provider, which
      // is the ordinary case, and the screen should say what it would take rather than show an error.
      if (e instanceof ForbiddenError) {
        setForbidden(true);
        return;
      }
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not load financing.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (forbidden) {
    return (
      <div className="mx-auto max-w-2xl space-y-5">
        <PageHeader title="Finance" subtitle="Fund borrowing requests secured on tokenized assets." />
        <EmptyState
          title="Financing is open to verified credit providers"
          body="To finance borrowing requests you need a verified business profile and an approved lending product. Financing arbitrary assets is not possible on this platform: you can only fund requests a borrower has opened."
          action={{ label: 'Back to Credit', href: '/credit' }}
        />
      </div>
    );
  }

  if (error) return <ErrorState body={error} onRetry={() => void load()} />;
  if (!opportunities || !portfolio) return <LoadingCard lines={5} />;

  const awaiting = offers.filter((o) => o.stage === 'awaiting_approval');

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <PageHeader
        title="Finance"
        subtitle="Borrowing requests open for financing, and the book you have built."
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Open requests" value={opportunities.length} />
        <StatTile label="Offers live" value={offers.filter((o) => o.stage === 'interest_expressed').length} />
        <StatTile label="Active loans" value={portfolio.summary.active} />
        <StatTile
          label="Outstanding"
          value={`KES ${formatWireAmount(portfolio.summary.outstanding_minor)}`}
        />
      </div>

      {/* The one thing a financier must not miss: a borrower has accepted and is waiting. */}
      {awaiting.length > 0 && (
        <Reveal>
          <section className="rounded-card border border-brand/30 bg-brand/5 p-4">
            <p className="text-sm font-medium text-ink">
              {awaiting.length} borrower{awaiting.length === 1 ? ' has' : 's have'} accepted your
              terms
            </p>
            <p className="mt-1 text-micro text-ink-muted">
              Complete your final approval to create the loan. Nothing is committed until you do.
            </p>
            <ul className="mt-3 space-y-2">
              {awaiting.map((o) => (
                <li key={o.id}>
                  <Link
                    to={`/credit/finance/${o.request_id}`}
                    className="flex items-center justify-between rounded-field bg-surface px-3 py-2 text-sm transition hover:bg-surface-sunken"
                  >
                    <span className="min-w-0 truncate text-ink">
                      {o.request_reference} · {o.product_name}
                    </span>
                    <span className="tabular shrink-0 text-micro font-medium text-brand">
                      KES {formatWireAmount(o.amount_minor)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        </Reveal>
      )}

      <nav className="flex gap-1.5" role="tablist">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={`rounded-field px-3 py-1.5 text-micro font-medium transition ${
              tab === t ? 'bg-primary text-white' : 'bg-surface-sunken text-ink hover:bg-border'
            }`}
          >
            {t}
          </button>
        ))}
      </nav>

      {tab === 'Opportunities' && (
        <section>
          {opportunities.length === 0 ? (
            <EmptyState
              title="No requests are open for financing"
              body="Borrowers open requests when they want financing against an asset they have tokenized. New requests appear here as soon as the Trust has verified their collateral."
            />
          ) : (
            <ul className="space-y-3">
              {opportunities.map((o) => (
                <li key={o.request_id}>
                  <Reveal>
                    <Link
                      to={`/credit/finance/${o.request_id}`}
                      className="block rounded-card border border-border bg-surface p-4 shadow-card transition hover:border-primary/40"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-ink">
                            {o.product.name}
                          </p>
                          <p className="text-nano text-ink-subtle">
                            {o.reference} · Tier {o.borrower.tier} borrower ·{' '}
                            {o.borrower.risk_state} risk
                          </p>
                        </div>
                        {o.my_offers > 0 && <StatusPill status="submitted" label="You have offered" />}
                      </div>

                      <p className="mt-2 line-clamp-2 text-micro text-ink-muted">{o.purpose}</p>

                      <dl className="mt-3 grid grid-cols-2 gap-3 border-t border-border pt-3 sm:grid-cols-4">
                        <Fact label="Requested" value={`KES ${formatWireAmount(o.amount_minor)}`} />
                        <Fact
                          label="Collateral"
                          value={`KES ${formatWireAmount(o.eligible_collateral_minor)}`}
                        />
                        <Fact label="LTV" value={`${(o.ltv_bps / 100).toFixed(1)}%`} />
                        <Fact label="Tenor" value={`${o.tenor_months} months`} />
                      </dl>

                      <div className="mt-2.5 flex items-center justify-between text-nano text-ink-subtle">
                        <span>
                          {o.collateral.length} asset{o.collateral.length === 1 ? '' : 's'} pledged ·{' '}
                          {o.live_offers} offer{o.live_offers === 1 ? '' : 's'} so far
                        </span>
                        {o.financing_deadline && (
                          <span>Closes {fmtDateShort(o.financing_deadline)}</span>
                        )}
                      </div>
                    </Link>
                  </Reveal>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {tab === 'My financing' && (
        <section>
          {offers.length === 0 ? (
            <EmptyState
              title="You have not offered on anything yet"
              body="Open a request from Opportunities and express interest with your terms."
            />
          ) : (
            <ul className="space-y-2.5">
              {offers.map((o) => (
                <li key={o.id}>
                  <Link
                    to={`/credit/finance/${o.request_id}`}
                    className="block rounded-card border border-border bg-surface p-3.5 shadow-card transition hover:border-primary/40"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-ink">
                          {o.request_reference} · {o.product_name}
                        </p>
                        <p className="text-nano text-ink-subtle">
                          {(o.rate_bps / 100).toFixed(2)}% over {o.tenor_months} months
                        </p>
                      </div>
                      <StatusPill status={o.stage} label={STAGE_LABEL[o.stage] ?? o.stage} />
                    </div>
                    <div className="mt-2.5 flex items-center justify-between border-t border-border pt-2.5 text-micro">
                      <span className="tabular text-ink">
                        KES {formatWireAmount(o.amount_minor)}
                      </span>
                      <span className="tabular text-ink-subtle">
                        KES {formatWireAmount(o.total_repayable_minor)} repayable
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {tab === 'Portfolio' && (
        <section className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatTile label="Disbursed" value={`KES ${formatWireAmount(portfolio.summary.disbursed_minor)}`} />
            <StatTile label="Repaid" value={portfolio.summary.repaid} />
            <StatTile label="In arrears" value={portfolio.summary.in_arrears} accent={portfolio.summary.in_arrears > 0} />
            <StatTile label="Recovered" value={portfolio.summary.recovered} />
          </div>

          <div>
            <SectionTitle>Collateral securing your book</SectionTitle>
            {portfolio.collateral.length === 0 ? (
              <EmptyState title="Nothing pledged to you yet" body="Collateral appears here once a loan you financed is live." />
            ) : (
              <ul className="space-y-2">
                {portfolio.collateral.map((c) => (
                  <li
                    key={c.position_id}
                    className="flex items-start justify-between gap-3 rounded-card border border-border bg-surface p-3.5"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-ink">{c.asset_name}</p>
                      <p className="text-nano text-ink-subtle">
                        {(c.pledged_bps / 100).toFixed(2)}% pledged · KES{' '}
                        {formatWireAmount(c.eligible_minor)} eligible
                      </p>
                    </div>
                    <StatusPill status={c.state} />
                  </li>
                ))}
              </ul>
            )}
          </div>

          {portfolio.recoveries.length > 0 && (
            <div>
              <SectionTitle>Recoveries</SectionTitle>
              <ul className="space-y-2">
                {portfolio.recoveries.map((r) => (
                  <li
                    key={r.enforcement_id}
                    className="rounded-card border border-border bg-surface p-3.5"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <p className="min-w-0 truncate text-sm text-ink">{r.reason}</p>
                      <StatusPill status={r.stage} />
                    </div>
                    <p className="mt-1.5 text-nano text-ink-subtle">
                      KES {formatWireAmount(r.outstanding_at_declaration_minor)} outstanding at
                      declaration · {fmtDateShort(r.declared_at)}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-nano text-ink-subtle">{label}</dt>
      <dd className="tabular truncate text-micro font-medium text-ink">{value}</dd>
    </div>
  );
}
